import { usePage } from "@inertiajs/react"
import { Loader2, Mic } from "lucide-react"
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from "react"
import { toast } from "sonner"
import { ComposerIconButton } from "@/components/ui/composer-icon-button"
import { VoiceInputMenu } from "@/components/voice/voice-input-menu"
import { useAudioInputPicker } from "@/hooks/use-audio-input-picker"
import { useAudioRecorder } from "@/hooks/use-audio-recorder"
import { useMicLevel } from "@/hooks/use-mic-level"
import { useTranslation } from "@/hooks/use-translation"
import { postAgentTranscribe, postAgentTranscribeWarmup, type AgentApiScope } from "@/lib/agent-api"
import {
  dictationErrorKey,
  getHoldToRecordPrefSnapshot,
  resolveTranscribeTimeoutMs,
  subscribeHoldToRecordPref,
  VOICE_DICTATION_MAX_SECONDS,
  VOICE_DICTATION_MIN_SECONDS,
  VoiceTranscribeError,
  writeHoldToRecordPref,
} from "@/lib/voice-dictation"
import { cn } from "@/lib/utils"
import type { SharedProps } from "@/types"

const getServerHoldToRecordPref = () => true

type Props = {
  projectId: number
  agentScope?: AgentApiScope
  /** Распознанный текст. Вставлять в поле, а не отправлять — пользователь вычитывает сам. */
  onText: (text: string) => void
  disabled?: boolean
  className?: string
  iconClassName?: string
}

export function VoiceDictationButton({
  projectId,
  agentScope,
  onText,
  disabled = false,
  className,
  iconClassName,
}: Props) {
  const { t } = useTranslation("common")
  const [transcribing, setTranscribing] = useState(false)
  const holdToRecord = useSyncExternalStore(
    subscribeHoldToRecordPref,
    getHoldToRecordPrefSnapshot,
    getServerHoldToRecordPref,
  )
  const abortRef = useRef<AbortController | null>(null)
  // Отмену по таймауту показываем, отмену из-за ухода со страницы — нет.
  const timedOutRef = useRef(false)
  const unmountedRef = useRef(false)
  const onTextRef = useRef(onText)
  onTextRef.current = onText
  // Предел ожидания приходит с сервера (потолок таймаута STT + запас): браузер
  // обязан сдаваться позже Rails, иначе расшифровка забиллится и потеряется.
  const { voice_transcribe_timeout_ms } = usePage().props as SharedProps
  const transcribeTimeoutMs = resolveTranscribeTimeoutMs(voice_transcribe_timeout_ms)

  const handleRecorded = useCallback(
    async (file: File) => {
      const controller = new AbortController()
      abortRef.current = controller
      timedOutRef.current = false
      const timer = window.setTimeout(() => {
        timedOutRef.current = true
        controller.abort()
      }, transcribeTimeoutMs)
      setTranscribing(true)
      try {
        const text = await postAgentTranscribe(agentScope ?? projectId, file, { signal: controller.signal })
        if (text === "") {
          toast.error(t("voiceDictation.errorNoSpeech"))
          return
        }
        onTextRef.current(text)
      } catch (e) {
        // Ушли со страницы во время распознавания — это не ошибка пользователя,
        // молчим (иначе всплывёт тост уже после того, как он закрыл экран).
        if (unmountedRef.current) return
        if (e instanceof DOMException && e.name === "AbortError") {
          if (timedOutRef.current) toast.error(t("voiceDictation.errorFailed"))
        } else if (e instanceof VoiceTranscribeError && e.errorCode === "spend_limit_exceeded") {
          toast.error(e.message)
        } else {
          toast.error(t(dictationErrorKey(e instanceof VoiceTranscribeError ? e.errorCode : undefined)))
        }
      } finally {
        window.clearTimeout(timer)
        abortRef.current = null
        if (!unmountedRef.current) setTranscribing(false)
      }
    },
    [projectId, agentScope, t, transcribeTimeoutMs],
  )

  // Захват звука вкладки надиктовке не нужен: распознаём то, что говорят в микрофон.
  const picker = useAudioInputPicker({
    allowSystemAudio: false,
    onDeviceUnavailable: () => toast.warning(t("voiceDictation.microphoneUnavailable")),
  })

  const recorder = useAudioRecorder({
    minSeconds: VOICE_DICTATION_MIN_SECONDS,
    maxSeconds: VOICE_DICTATION_MAX_SECONDS,
    // Без обратного отсчёта: нажал — говоришь.
    countdownSeconds: 0,
    source: picker.source,
    ...picker.recorderProps,
    onRecorded: (file) => {
      void handleRecorded(file)
    },
    onTooShort: () => toast.error(t("voiceDictation.errorTooShort")),
    onPermissionDenied: () => toast.error(t("voiceDictation.errorPermission")),
    onError: () => toast.error(t("voiceDictation.errorMicUnavailable")),
  })

  const recording = recorder.phase === "recording"
  const busy = transcribing || recorder.isBusy
  const micFillRef = useRef<HTMLSpanElement>(null)
  useMicLevel(recording ? recorder.stream : null, micFillRef)

  useEffect(() => {
    return () => {
      unmountedRef.current = true
      abortRef.current?.abort()
    }
  }, [])

  // Спящий self-hosted Whisper будим в момент старта записи: контейнер поднимается, пока
  // пользователь говорит, и после отпускания кнопки ждать его остаётся на длину записи меньше.
  const warmUp = useCallback(() => {
    postAgentTranscribeWarmup(agentScope ?? projectId)
  }, [agentScope, projectId])

  const toggle = useCallback(() => {
    // В режиме удержания запись ведут pointer/key-хендлеры: click прилетает уже
    // после отпускания и включил бы её заново.
    if (holdToRecord || transcribing) return
    if (recording) {
      recorder.stop()
    } else {
      warmUp()
      recorder.start()
    }
  }, [holdToRecord, recorder, recording, transcribing, warmUp])

  /** Кнопку держат прямо сейчас: второй pointerdown/keydown повторно запись не поднимает. */
  const holdingRef = useRef(false)
  /**
   * getUserMedia ещё не вернулся. Диалог прав шлёт pointercancel / lostpointercapture —
   * это не отпускание, и cancel() здесь бросил бы первый старт.
   */
  const awaitingCaptureRef = useRef(false)

  const beginHold = useCallback(() => {
    if (holdingRef.current || disabled || transcribing) return
    holdingRef.current = true
    awaitingCaptureRef.current = true
    warmUp()
    void recorder.start()
  }, [disabled, recorder, transcribing, warmUp])

  const endHold = useCallback(() => {
    if (!holdingRef.current) return
    holdingRef.current = false
    awaitingCaptureRef.current = false
    // Отпустили раньше, чем браузер отдал поток (диалог прав, перечисление устройств):
    // stop() в фазе idle ничего не сделает, а cancel() снимет незавершённую попытку —
    // иначе запись включилась бы уже после того, как кнопку отпустили.
    if (recorder.phase === "recording") recorder.stop()
    else recorder.cancel()
  }, [recorder])

  const endHoldIfReleased = useCallback(() => {
    if (awaitingCaptureRef.current && recorder.phase !== "recording") return
    endHold()
  }, [endHold, recorder.phase])

  // Захват начался — указатель уже не «ждём права». Дальше pointerup (на кнопке или
  // на window) останавливает запись, даже если диалог прав украл capture.
  useEffect(() => {
    if (recording) awaitingCaptureRef.current = false
  }, [recording])

  // Режим переключили посреди удержания — доводим начатую запись до распознавания,
  // иначе holdingRef застрянет в true и кнопка перестанет реагировать.
  // Обратно (click-запись → включили hold): пользователя уже нет на кнопке, поэтому
  // помечаем сессию как удержание — следующий pointerup/клик её остановит.
  useEffect(() => {
    if (!holdToRecord) {
      endHold()
      return
    }
    if (recorder.phase === "recording") holdingRef.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps -- только смена режима; phase читаем в этот момент
  }, [holdToRecord])

  // Esc отменяет запись, не отправляя её на распознавание.
  useEffect(() => {
    if (!recording) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return
      e.preventDefault()
      holdingRef.current = false
      awaitingCaptureRef.current = false
      recorder.cancel()
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [recorder, recording])

  // Space/Enter и pointerup должны доходить даже если фокус ушёл (Alt-Tab, клик
  // в диалог прав). visibilitychange — ушли со вкладки, запись не должна висеть.
  useEffect(() => {
    if (!holdToRecord) return
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key !== " " && e.key !== "Enter") return
      if (!holdingRef.current) return
      e.preventDefault()
      endHold()
    }
    const onPointerUp = () => {
      if (!holdingRef.current) return
      endHoldIfReleased()
    }
    const onVisibility = () => {
      if (document.visibilityState === "hidden") endHold()
    }
    window.addEventListener("keyup", onKeyUp)
    window.addEventListener("pointerup", onPointerUp)
    document.addEventListener("visibilitychange", onVisibility)
    return () => {
      window.removeEventListener("keyup", onKeyUp)
      window.removeEventListener("pointerup", onPointerUp)
      document.removeEventListener("visibilitychange", onVisibility)
    }
  }, [endHold, endHoldIfReleased, holdToRecord])

  const label = transcribing
    ? t("voiceDictation.transcribing")
    : recording
      ? t("voiceDictation.stop")
      : holdToRecord
        ? t("voiceDictation.startHold")
        : t("voiceDictation.start")

  // На распознавании глиф сменяется спиннером. Во время записи микрофон остаётся
  // микрофоном того же размера — кнопка не прыгает под курсором, особенно в режиме
  // удержания. Громкость видна заливкой иконки снизу вверх.
  const icon = transcribing ? (
    <Loader2 className={cn("h-4 w-4 animate-spin", iconClassName)} />
  ) : recording ? (
    <span ref={micFillRef} aria-hidden className="relative inline-flex h-4 w-4 [--mic-fill:0]">
      <Mic className={cn("h-4 w-4 opacity-40", iconClassName)} />
      <span className="absolute inset-0 [clip-path:inset(calc((1-var(--mic-fill))*100%)_0_0_0)]">
        <Mic className={cn("h-4 w-4", iconClassName)} />
      </span>
    </span>
  ) : (
    <Mic className={cn("h-4 w-4", iconClassName)} />
  )

  const holdHandlers = holdToRecord
    ? {
        onPointerDown: (e: ReactPointerEvent<HTMLButtonElement>) => {
          if (e.button !== 0) return
          // Захват указателя: палец/курсор уезжает с кнопки — pointerup всё равно наш,
          // иначе запись осталась бы включённой навсегда.
          e.currentTarget.setPointerCapture(e.pointerId)
          beginHold()
        },
        onPointerUp: () => {
          awaitingCaptureRef.current = false
          endHold()
        },
        onPointerCancel: endHoldIfReleased,
        onLostPointerCapture: endHoldIfReleased,
        onKeyDown: (e: ReactKeyboardEvent<HTMLButtonElement>) => {
          if (e.key !== " " && e.key !== "Enter") return
          // Иначе пробел прокручивает страницу, а автоповтор сыплет keydown пачкой.
          e.preventDefault()
          if (e.repeat) return
          beginHold()
        },
        onKeyUp: (e: ReactKeyboardEvent<HTMLButtonElement>) => {
          if (e.key !== " " && e.key !== "Enter") return
          e.preventDefault()
          endHold()
        },
        // Долгое нажатие на тачскрине иначе откроет контекстное меню поверх записи.
        onContextMenu: (e: ReactMouseEvent<HTMLButtonElement>) => e.preventDefault(),
      }
    : {}

  const shared = {
    type: "button" as const,
    onClick: toggle,
    disabled: disabled || transcribing,
    "aria-label": label,
    "aria-pressed": recording,
    ...holdHandlers,
  }
  const tone = cn(
    // Запись и распознавание — одно состояние «занят»: подсветка не мигает на стыке,
    // меняется только глиф. Акцентом, а не красным: идёт работа, а не сбой.
    //
    // Заливка, а не мягкий тон: пара accent-soft/accent-text на тёмной теме почти
    // не видна — #ede9fe глаз не отличает от обычного #d4d4d8, а 20% фиолетового
    // на почти чёрном не читается. Живой микрофон — состояние, которое нельзя
    // проглядеть, поэтому секция заливается акцентом целиком.
    busy && "bg-ui-accent text-ui-on-accent hover:bg-ui-accent-hover hover:text-ui-on-accent",
    // Пока держат кнопку, страница не должна скроллиться от свайпа и выделять текст.
    holdToRecord && "touch-none select-none",
    className,
  )

  const inputMenu = (
    <VoiceInputMenu
      devices={picker.devices}
      value={picker.value}
      onSelect={picker.select}
      pendingLabel={picker.pendingLabel}
      defaultDeviceId={picker.defaultDeviceId}
      holdToRecord={holdToRecord}
      onHoldToRecordChange={writeHoldToRecordPref}
      disabled={busy}
    />
  )

  return (
    <span className="inline-flex shrink-0 items-center">
      {/* Без рамки и разделителя: в композере кнопка соседствует с текстом, и лишняя
          обводка там только шумит. Каждая иконка получает свою подложку по наведению —
          цель клика видно ровно тогда, когда она нужна. */}
      <ComposerIconButton {...shared} className={tone}>
        {icon}
      </ComposerIconButton>
      {inputMenu}
      {/* Дискретные состояния для скринридера: таймер озвучивать нельзя — затараторит. */}
      <span className="sr-only" role="status" aria-live="polite">
        {recording ? t("voiceDictation.recording") : transcribing ? t("voiceDictation.transcribing") : ""}
      </span>
    </span>
  )
}
